/**
 * BDL-1609: decide whether a running dev server's JS bundle still reflects
 * the source on disk.
 *
 * WHY AN mtime COMPARISON AND NOT A GIT-REV COMPARISON. The obvious design
 * -- bake a rev into the bundle and compare it to `git rev-parse HEAD` --
 * false-positives constantly on this repo, because HEAD moves every few
 * minutes and most commits never touch admin-app's watched set (a
 * backend-only or docs-only commit leaves the bundle genuinely correct while
 * the revs differ). An alarm that fires on a healthy server trains people to
 * ignore it, which is the failure mode the repo's Gate-Rollout Rule exists to
 * prevent. So revs are reported as CONTEXT only and never trip the verdict.
 *
 * The comparison below measures the EFFECT instead of guessing at a cause,
 * and it is PER FILE: "is this file newer than the last compile that
 * actually READ this file?" That is true for every known way to arrive at a
 * stale bundle -- the DISABLE_AUTO_RELOAD opt-out, a watcher that wedged
 * mid-uptime, or a merge whose writes never raised a watch event -- and it
 * is false on a healthy server, which rebuilds within ~1s of any change.
 *
 * ROUND 3 CORRECTION, and the reason "per file" is load-bearing rather than
 * pedantic. This was ONE GLOBAL CLOCK stamped by every completed compile.
 * rsbuild dev compiles LAZILY, so merely navigating the app completes a
 * compile that recompiles nothing already built -- and that stamp moved the
 * clock past an un-ingested edit, flipping a correct `stale` to `fresh`
 * while the served bytes were unchanged. QA measured it: two edits on disk,
 * zero of them in the bundle the browser was running, verdict `fresh`. The
 * clock answered "when did A compile finish", never "when was THIS file
 * read". See ./devBundleFreshnessDeps.ts `advanceBuildCoverage`.
 *
 * THE FILE SET IS RSPACK'S OWN `compilation.fileDependencies`, not a
 * directory walk. Round 1 walked the ignore-pattern allowlist, which is a
 * strict superset of the module graph, so touching any file that was merely
 * NEAR the build -- a unit test, a report artifact, a regenerated
 * `shared/config.js` -- reported STALE on a server whose bundle was entirely
 * correct. See ./devBundleFreshnessDeps.ts.
 *
 * WHAT THIS DOES NOT COVER, stated so nobody reads it as more: it answers
 * only "this bundle was built from an older tree" (layer i). It cannot see
 * a browser holding an old chunk via a 304 (layer ii), and it cannot see a
 * long-running server serving a module's stale export list while the rest of
 * the build is current (layer iii) -- in that mode the build timestamp is
 * current while one module is not, so this returns `fresh` on a genuinely
 * poisoned server. Both are documented in admin-app/CLAUDE.md "Known
 * Gotchas"; only a server restart clears them.
 */

/** Default rebuild grace: an edit newer than the last build by less than
 * this is treated as a rebuild in flight rather than a stale bundle. */
export const DEFAULT_FRESHNESS_GRACE_MS = 10_000;

/** Dev-server route that reports the raw inputs below. Shared by the
 * middleware that serves it and the client helper that reads it. */
export const DEV_FRESHNESS_ENDPOINT = '/__bundle-freshness';

export interface DevBundleFreshnessInput {
	/** When a compile last actually READ the file named by
	 * `newestSourcePath` -- NOT a global "some compile finished" clock. A
	 * lazy compile that never re-read that file must not advance this, or it
	 * clears an alarm it did not earn. Null before the first build. */
	lastBuildAtMs: number | null;
	/** mtime of the file that has outrun its own coverage by the most. Null
	 * if the build reported no dependency set, or none could be read. */
	newestSourceMtimeMs: number | null;
	newestSourcePath?: string | null;
	/** Whether DISABLE_AUTO_RELOAD switched the watcher off for this server. */
	autoReloadDisabled: boolean;
	graceMs?: number;
}

export interface DevBundleFreshnessResult {
	/** `unknown` is a real outcome and is never a pass -- an unanswered
	 * question must not read the same as a clean one. */
	verdict: 'fresh' | 'stale' | 'unknown';
	stale: boolean;
	autoReloadDisabled: boolean;
	/** How far the newest source outran the build, ms. Null unless stale. */
	staleByMs: number | null;
	reason: string;
}

export function evaluateDevBundleFreshness(
	input: DevBundleFreshnessInput,
): DevBundleFreshnessResult {
	const {
		lastBuildAtMs,
		newestSourceMtimeMs,
		newestSourcePath,
		autoReloadDisabled,
		graceMs = DEFAULT_FRESHNESS_GRACE_MS,
	} = input;

	const watchNote = autoReloadDisabled
		? ' (DISABLE_AUTO_RELOAD is set, so this server will NOT rebuild)'
		: '';

	if (lastBuildAtMs === null || lastBuildAtMs === undefined) {
		return {
			verdict: 'unknown',
			stale: false,
			autoReloadDisabled,
			staleByMs: null,
			reason: `the dev server has not reported a completed build yet, so bundle age is unknown${watchNote}`,
		};
	}

	if (
		newestSourceMtimeMs === null ||
		newestSourceMtimeMs === undefined
	) {
		return {
			verdict: 'unknown',
			stale: false,
			autoReloadDisabled,
			staleByMs: null,
			reason: `the build reported no readable dependency set, so bundle age is unknown${watchNote}`,
		};
	}

	const staleByMs = newestSourceMtimeMs - lastBuildAtMs;

	if (staleByMs > graceMs) {
		const seconds = Math.round(staleByMs / 1000);
		const which = newestSourcePath || 'a file this build depends on';
		return {
			verdict: 'stale',
			stale: true,
			autoReloadDisabled,
			staleByMs,
			reason: `${which} is ${seconds}s newer than the last compile that read it -- the served bundle does NOT contain it${watchNote}`,
		};
	}

	return {
		verdict: 'fresh',
		stale: false,
		autoReloadDisabled,
		staleByMs: null,
		reason: `every file this build depends on was read by a compile newer than it${watchNote}`,
	};
}
